import type { NewTriviaSettings, Theme } from './types';

export type PreviewScreen = 'global' | 'login' | 'question' | 'category' | 'final' | 'leaderboard';
export type PreviewViewport = 'mobile' | 'tablet' | 'desktop';
export type PreviewAnswerState = 'default' | 'selected' | 'correct' | 'incorrect' | 'disabled';

export const embeddedPreviewReadyType = 'new-trivia:preview-ready';
export const embeddedPreviewUpdateType = 'new-trivia:preview-update';

export interface EmbeddedPreviewUpdate {
	theme?: Partial<Theme>;
	categorySelectorSettings?: Partial<NewTriviaSettings['categorySelectorSettings']>;
	gameLabels?: Partial<Pick<NewTriviaSettings,
		| 'categoryClashBoardTitle'
		| 'categoryClashQuestionsCompletedLabel'
		| 'categoryClashScoreLabel'
		| 'categoryClashButtonLabel'
		| 'bingoTitle'
		| 'bingoDescription'
		| 'bingoSubmitButtonLabel'
		| 'bingoAnsweredLabel'
		| 'bingoCorrectLabel'
		| 'bingoScoreLabel'
		| 'bingoBonusLabel'
		| 'bingoBonusPointsLabel'
		| 'bingoBonusPointsDescription'
	>>;
	answerState?: PreviewAnswerState;
}

export interface PreviewOptions {
	enabled: boolean;
	embedded: boolean;
	screen: PreviewScreen;
	viewport: PreviewViewport;
	previewSession: string;
}

const screens: PreviewScreen[] = ['global', 'login', 'question', 'category', 'final', 'leaderboard'];
const viewports: PreviewViewport[] = ['mobile', 'tablet', 'desktop'];
const answerStates: PreviewAnswerState[] = ['default', 'selected', 'correct', 'incorrect', 'disabled'];
const themeKeys: Array<keyof Theme> = [
	'engineVersion', 'mode', 'personality', 'primaryColor', 'secondaryColor', 'accentColor', 'backgroundColor', 'textColor',
	'successColor', 'errorColor', 'fontFamily', 'secondaryFontFamily', 'buttonFontFamily', 'leaderboardFontFamily',
	'buttonStyle', 'buttonRadius', 'inputRadius', 'buttonBorderWidth', 'cardRadius', 'heroRadius', 'topLogoUrl', 'gameLogoUrl', 'backgroundImageUrl',
	'appBackground', 'surfaceBackground', 'secondarySurfaceBackground', 'elevatedSurface', 'inputBackground', 'cardBackground',
	'borderColor', 'inputBorder', 'divider', 'inputText', 'mutedTextColor', 'primaryText', 'secondaryText', 'placeholderText',
	'focusGlow', 'linkHoverColor', 'loginCardBackground',
	'headerBackground', 'pillBackground', 'pillText', 'pillBorder',
	'loginTextColor', 'loginLinkColor', 'heroTitleColor', 'heroSecondaryTextColor', 'heroScrimColor', 'primaryButtonBackground',
	'primaryButtonText', 'primaryButtonHover', 'primaryButtonBorder', 'primaryButtonActiveShadow', 'primaryButtonGradient',
	'secondaryButtonBackground', 'secondaryButtonText', 'secondaryButtonBorder', 'secondaryButtonHover', 'disabledOpacity',
	'allowCardGradients', 'cardGradientEnd', 'heroGradientEnd', 'heroGradientAngle', 'shadowColor', 'softShadowColor', 'inputShadow',
	'shadowBlurMultiplier', 'shadowDepthMultiplier', 'overlayColor', 'questionPanelBackground', 'unselectedBackground',
	'unselectedText', 'unselectedBorder', 'unselectedGlow', 'answerLetterUnselectedBackground', 'answerLetterUnselectedText',
	'answerLetterUnselectedBorder',
	'answerSelectedBackground', 'answerCorrectBackground', 'answerIncorrectBackground', 'answerLetterSelectedBackground',
	'answerLetterSelectedText', 'answerLetterSelectedBorder', 'selectedText', 'selectedBorder', 'selectedGlow', 'selectedBackground',
	'answerLetterCorrectBackground', 'answerLetterCorrectText', 'answerLetterCorrectBorder', 'correctText', 'correctBorder',
	'correctGlow', 'correctBackground', 'answerLetterIncorrectBackground', 'answerLetterIncorrectText',
	'answerLetterIncorrectBorder', 'incorrectText', 'incorrectBorder', 'incorrectGlow', 'incorrectBackground',
	'questionFeedbackIncorrectBackground', 'questionFeedbackIncorrectBorder', 'questionFeedbackIncorrectText', 'categoryPalette',
	'questionImageBackground', 'categoryLabelBackground', 'categoryLabelText', 'categoryClashBoardTitleColor', 'categoryClashQuestionsCompletedLabelColor', 'categoryClashBoardBackground', 'categoryClashCategoryBackground',
	'categoryClashCategoryBorder',
	'categoryClashCategoryText', 'categoryClashTileBackground', 'categoryClashTileText',
	'categoryClashHeaderStatsBackground', 'categoryClashHeaderStatsBorder',
	'categoryClashHeaderStatsText', 'bingoBoardBackground', 'bingoBoardBorder', 'bingoTileBackground', 'bingoTileText', 'bingoLineColor',
	'finalCardBackground', 'finalScoreColor', 'finalActionBackground', 'finalActionText', 'summaryItemBackground',
	'summaryItemLabelText', 'summaryItemBorder', 'leaderboardBackground', 'leaderboardTextColor', 'leaderboardRowBackground',
	'leaderboardRowText', 'leaderboardRowBorder', 'leaderboardYouBackground', 'leaderboardYouText', 'leaderboardYouBorder',
];
const selectorSettingKeys: Array<keyof NewTriviaSettings['categorySelectorSettings']> = [
	'cardColor', 'cardImageUrl', 'wheelColors', 'wheelFontColor', 'slotFontColor', 'slotBarrelColor', 'slotMachineColor',
];
const gameLabelKeys = [
	'categoryClashBoardTitle', 'categoryClashQuestionsCompletedLabel', 'categoryClashScoreLabel', 'categoryClashButtonLabel',
	'bingoTitle', 'bingoDescription', 'bingoSubmitButtonLabel', 'bingoAnsweredLabel', 'bingoCorrectLabel', 'bingoScoreLabel',
	'bingoBonusLabel', 'bingoBonusPointsLabel', 'bingoBonusPointsDescription',
] as const;

const allowedValue = <T extends string>(value: string | null, allowed: T[], fallback: T): T =>
	allowed.includes(value as T) ? value as T : fallback;

export const readPreviewOptions = (search: string): PreviewOptions => {
	const query = new URLSearchParams(search);
	return {
		enabled: query.get('preview') === '1',
		embedded: query.get('embedded') === '1',
		screen: allowedValue(query.get('screen'), screens, 'login'),
		viewport: allowedValue(query.get('viewport'), viewports, 'mobile'),
		previewSession: (query.get('previewSession') ?? '').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 80),
	};
};

export const buildEmbeddedPreviewUrl = (href: string, options: PreviewOptions): string => {
	const url = new URL(href);
	url.searchParams.set('preview', '1');
	url.searchParams.set('screen', options.screen);
	url.searchParams.set('viewport', options.viewport);
	url.searchParams.set('embedded', '1');
	return url.toString();
};

const recordValue = (value: unknown): Record<string, unknown> | null =>
	typeof value === 'object' && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : null;

const stringArray = (value: unknown): string[] | null =>
	Array.isArray(value) && value.every((item) => typeof item === 'string') ? value : null;

export const readEmbeddedPreviewUpdate = (value: unknown): EmbeddedPreviewUpdate | null => {
	const message = recordValue(value);
	if (!message || message.type !== embeddedPreviewUpdateType) return null;
	const payload = recordValue(message.payload);
	if (!payload) return null;

	const update: EmbeddedPreviewUpdate = {};
	const answerState = payload.answerState;
	if (typeof answerState === 'string' && answerStates.includes(answerState as PreviewAnswerState)) {
		update.answerState = answerState as PreviewAnswerState;
	}
	const sourceTheme = recordValue(payload.theme);
	if (sourceTheme) {
		const theme: Record<string, unknown> = {};
		for (const key of themeKeys) {
			const item = sourceTheme[key];
			if (key === 'categoryPalette') {
				const colors = stringArray(item);
				if (colors) theme[key] = colors;
			}
			else if (['engineVersion', 'buttonRadius', 'inputRadius', 'buttonBorderWidth', 'cardRadius', 'heroRadius', 'disabledOpacity', 'shadowBlurMultiplier', 'shadowDepthMultiplier'].includes(key)) {
				if (typeof item === 'number' && Number.isFinite(item)) theme[key] = item;
			}
			else if (key === 'allowCardGradients') {
				if (typeof item === 'boolean') theme[key] = item;
			}
			else if (typeof item === 'string') theme[key] = item;
		}
		update.theme = theme as Partial<Theme>;
	}

	const sourceSettings = recordValue(payload.categorySelectorSettings);
	if (sourceSettings) {
		const settings: Record<string, unknown> = {};
		for (const key of selectorSettingKeys) {
			const item = sourceSettings[key];
			if (key === 'wheelColors') {
				const colors = stringArray(item);
				if (colors) settings[key] = colors;
			}
			else if (typeof item === 'string') settings[key] = item;
		}
		update.categorySelectorSettings = settings as Partial<NewTriviaSettings['categorySelectorSettings']>;
	}
	const sourceLabels = recordValue(payload.gameLabels);
	if (sourceLabels) {
		const labels: Record<string, string> = {};
		for (const key of gameLabelKeys) {
			if (typeof sourceLabels[key] === 'string') labels[key] = sourceLabels[key];
		}
		update.gameLabels = labels;
	}

	return update;
};
